/**
 * Estilos del modal del SDK (R2, §8 plan — modal UI).
 *
 * Se exportan como un **string CSS** inyectado una sola vez en `<head>` vía
 * `injectStyles()` (idempotente). Sin imports de `.css` (invariante §2:
 * sin dependencias runtime, bundle < 15 KB gzip). Las clases van prefijadas
 * `finva-modal-*` para no colisionar con los estilos del sitio host.
 *
 * Animación (patrón transitions-dev `data-open`):
 *  - Overlay: fade (opacity) entre `[data-open="false"]` y `[data-open="true"]`.
 *  - Tarjeta: translateY(16px) + scale(.98) + opacity 0 → translateY(0)
 *    scale(1) opacity 1.
 *  - Abrir: `250ms cubic-bezier(0.22, 1, 0.36, 1)`.
 *  - Cerrar: `150ms ease-out`.
 *
 * `prefers-reduced-motion: reduce` → `transition: none !important` (cambio
 * instantáneo, sin animación).
 */

export const MODAL_STYLES_ID = 'finva-modal-styles';

/** String CSS del modal. Exportado por si un consumidor quiere inspeccionarlo. */
export const MODAL_CSS = `
/* El modal se inyecta en la página host, cuyo reset no controlamos. Forzamos
   border-box en nuestros nodos para que padding no infle sus alturas (sin esto,
   el header crecía bastante más de lo esperado según los estilos del host). */
.finva-modal-overlay,
.finva-modal-overlay *,
.finva-modal-overlay *::before,
.finva-modal-overlay *::after {
  box-sizing: border-box;
}
.finva-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  transition: opacity 250ms cubic-bezier(0.22, 1, 0.36, 1);
}
.finva-modal-overlay[data-open="true"] {
  opacity: 1;
}
.finva-modal-overlay[data-open="false"] {
  opacity: 0;
  transition: opacity 150ms ease-out;
}

.finva-modal-card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 92vw;
  /* Coincide con el ancho de contenido del embed (.embed-credit__inner) para
     que el nav switch + tarjetas no se aprieten ni partan textos en el popup. */
  max-width: 520px;
  max-height: 90vh;
  /* dvh accounts for mobile browser chrome; ignored where unsupported. */
  max-height: 90dvh;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.22);
  opacity: 0;
  transform: translateY(16px) scale(0.98);
  transition:
    opacity 250ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 250ms cubic-bezier(0.22, 1, 0.36, 1);
}
.finva-modal-card[data-open="true"] {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.finva-modal-card[data-open="false"] {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
  transition:
    opacity 150ms ease-out,
    transform 150ms ease-out;
}

/* Scrollable body: the iframe grows with its content (finva:resize) and this
   wrapper scrolls once the card hits its max-height. */
.finva-modal-iframe-wrapper {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}
.finva-modal-iframe-wrapper > iframe {
  width: 100%;
  border: 0;
  display: block;
  margin: 0;
  padding: 0;
}

@media (prefers-reduced-motion: reduce) {
  .finva-modal-overlay,
  .finva-modal-card,
  .finva-modal-overlay[data-open="false"],
  .finva-modal-card[data-open="false"] {
    transition: none !important;
  }
}
`;

let injected = false;

/**
 * Inyecta el `<style>` del modal en `<head>` una sola vez. Idempotente: llamadas
 * repetidas no duplican el nodo. Devuelve `true` si esta llamada insertó el
 * estilo, `false` si ya estaba presente.
 */
export function injectStyles(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.getElementById(MODAL_STYLES_ID)) {
    injected = true;
    return false;
  }
  const style = document.createElement('style');
  style.id = MODAL_STYLES_ID;
  style.textContent = MODAL_CSS;
  document.head.appendChild(style);
  injected = true;
  return true;
}

/** Resetea el flag de inyección (útil para tests con jsdom entre casos). */
export function _resetStylesInjectedForTests(): void {
  injected = false;
  const existing = document.getElementById(MODAL_STYLES_ID);
  if (existing) existing.remove();
}